/* import __COLOCATED_TEMPLATE__ from './handoff-to-salesforce-case.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type SalesforceCaseDescriptionSource } from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type HandoffToSalesforceCaseType from 'embercom/models/operator/visual-builder/step/handoff-to-salesforce-case';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { isEmpty, isPresent } from '@ember/utils';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import { action } from '@ember/object';
import { BlocksDocument } from '@intercom/embercom-prosemirror-composer';
import createFragmentFromBlock from 'embercom/lib/blocks/create-fragment-from-block';
import { type Block } from '@intercom/interblocks.ts';
import type Store from '@ember-data/store';

export interface HandoffToSalesforceCaseArgs {
  step: HandoffToSalesforceCaseType;
  editorState: EditorState;
  readOnly: boolean;
  isTemplatePreview?: boolean;
  isHandoffToSalesforceCase?: boolean;
  settingsBannerHeading?: string;
}

export interface HandoffOption {
  text: string;
  value: string | number;
  isDisabled?: boolean;
}

export interface HandoffOptionGroup {
  heading: string;
  items: HandoffOption[];
}

export default class HandoffToSalesforceCase extends Component<HandoffToSalesforceCaseArgs> {
  @service declare intl: IntlService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare store: Store;

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isLoading(): boolean {
    return (
      this.finStandaloneService.isLoadingSalesforceSetupData ||
      this.finStandaloneService.isLoadingSalesforceConfig
    );
  }

  get handoverOptions(): HandoffOptionGroup[] {
    let admins = this.salesforceSetupData.admins?.map((admin) => ({
      text: admin.name,
      value: admin.id,
    }));
    let queues = this.salesforceSetupData.queues?.map((queue) => ({
      text: queue.name,
      value: queue.id,
    }));

    let options = [];

    if (isPresent(queues)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.queues-title',
        ),
        items: queues,
      });
    }

    if (isPresent(admins)) {
      options.push({
        heading: this.intl.t(
          'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.users-title',
        ),
        items: admins,
      });
    }

    if (isEmpty(options)) {
      options.push({
        heading: '',
        items: [
          {
            text: this.intl.t(
              'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.no-queues-or-users-available',
            ),
            value: '',
            isDisabled: true,
          },
        ],
      });
    }

    return options;
  }

  get selectedHandoverOption(): HandoffOption | undefined {
    let selectedHandoverOption = this.handoverOptions
      .flatMap((group) => group.items)
      .find((option) => option.value === this.args.step.handoffToSalesforceOwnerId);
    return selectedHandoverOption;
  }

  get settingsBannerHeading(): string {
    return (
      this.args.settingsBannerHeading ||
      this.intl.t(
        'operator.workflows.visual-builder.handoff-to-salesforce-case.settings.banner-heading',
      )
    );
  }

  get salesforceCaseSubjectBlocks(): BlocksDocument {
    let blocks = new BlocksDocument(this.args.step.salesforceCaseSubjectBlocks?.toArray() || []);
    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Case from Fin AI Agent with {{full_name}}',
        }),
      ]);
    }
    return blocks;
  }

  get salesforceCaseDescriptionBlocks(): BlocksDocument {
    let blocks = new BlocksDocument(
      this.args.step.salesforceCaseDescriptionBlocks?.toArray() || [],
    );
    if (this.shouldShowDefaultBlocks(blocks)) {
      blocks = new BlocksDocument([
        createFragmentFromBlock(this.store, {
          type: 'paragraph',
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          text: 'Created from Fin AI Agent',
        }),
      ]);
    }
    return blocks;
  }

  private shouldShowDefaultBlocks(blocks: BlocksDocument) {
    if (blocks.blocks.length === 0) {
      return true;
    }

    if (blocks.blocks.length === 1) {
      let block = blocks.blocks[0];
      return 'text' in block && typeof block.text === 'string' && block.text.trim() === '';
    }

    return false;
  }

  @action setHandoffToSalesforceOwnerId(handoffId: string) {
    this.args.step.handoffToSalesforceOwnerId = handoffId;
  }

  @action
  setCaseDescriptionSource(source: SalesforceCaseDescriptionSource) {
    this.args.step.salesforceCaseDescriptionSource = source;
  }

  @action
  onCaseSubjectChange(blockDocuments: BlocksDocument) {
    let blockFragments = blockDocuments.blocks.map((block: Block) =>
      createFragmentFromBlock(this.store, block),
    );

    this.args.step.salesforceCaseSubjectBlocks?.clear();
    this.args.step.salesforceCaseSubjectBlocks?.pushObjects(blockFragments);
  }

  @action
  onCaseDescriptionChange(blockDocuments: BlocksDocument) {
    let blockFragments = blockDocuments.blocks.map((block: Block) =>
      createFragmentFromBlock(this.store, block),
    );

    this.args.step.salesforceCaseDescriptionBlocks?.clear();
    this.args.step.salesforceCaseDescriptionBlocks?.pushObjects(blockFragments);
  }

  @action
  toggleShouldSetContactId(): void {
    this.args.step.shouldSetSalesforceContactId = !this.args.step.shouldSetSalesforceContactId;
  }

  @action
  toggleShouldCreateContact(): void {
    this.args.step.shouldCreateSalesforceContact = !this.args.step.shouldCreateSalesforceContact;
  }

  @action
  toggleShouldSetSalesforceAccountId(): void {
    this.args.step.shouldSetSalesforceAccountId = !this.args.step.shouldSetSalesforceAccountId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::HandoffToSalesforceCase': typeof HandoffToSalesforceCase;
    'workflows/graph-editor/node-items/steps/handoff-to-salesforce-case': typeof HandoffToSalesforceCase;
  }
}
